import React, { useEffect, useRef, useState } from 'react';
import { Controller, FieldErrors, useForm } from 'react-hook-form';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import HostExperienceSingleDatePicker from './HostExperienceSingleDatePicker';
import ErrorMessage from './ErrorMessage';

interface SessionDatePickerProps {
  errors: FieldErrors;
  isDayHighlighted: (date: Date) => boolean;
  onSessionDatePrevMonthClick: (date: Moment) => void;
  onSessionDateNextMonthClick: (date: Moment) => void;
  selectedSessionDateAvailabilityCalendar: Moment | null;
  setSelectedSessionDateAvailabilityCalendar: (date: Moment) => void;
  isCalendarLoading: boolean;
  clearErrors: (name?: string) => void;
}

const SingleSessionDatePicker: React.FC<SessionDatePickerProps> = ({
  errors,
  isDayHighlighted,
  onSessionDatePrevMonthClick,
  onSessionDateNextMonthClick,
  selectedSessionDateAvailabilityCalendar,
  setSelectedSessionDateAvailabilityCalendar,
  isCalendarLoading,
  clearErrors,
}) => {
  const { control } = useForm();
  const { t: translate } = useTranslation();
  const dateInputRef = useRef<HTMLDivElement>(null);
  const [localDate, setLocalDate] = useState(
    selectedSessionDateAvailabilityCalendar,
  );

  useEffect(() => {
    setLocalDate(selectedSessionDateAvailabilityCalendar);
  }, [selectedSessionDateAvailabilityCalendar]);

  const handleChange = (date: Date) => {
    setSelectedSessionDateAvailabilityCalendar(moment(date));
    clearErrors('sessionDate');
    return date;
  };

  const value = localDate instanceof moment ? localDate.toDate() : null;

  return (
    <div className="experience-view__single-calendar-wrapper">
      <Controller
        control={control}
        name="sessionDate"
        rules={{ required: true }}
        defaultValue={localDate}
        render={() => (
          <HostExperienceSingleDatePicker
            ref={dateInputRef}
            date={value}
            initialVisibleMonth={() => value ?? new Date()}
            onPrevMonthClick={(date) =>
              onSessionDatePrevMonthClick(moment(date))
            }
            onNextMonthClick={(date) =>
              onSessionDateNextMonthClick(moment(date))
            }
            isOutsideRange={(date) => !isDayHighlighted(date)}
            onChange={(selected: Date) => {
              handleChange(selected);
            }}
            isCalendarLoading={isCalendarLoading}
            aria-describedby="sessionDate"
            key={localDate?.valueOf()}
          />
        )}
      />
      <ErrorMessage
        errors={errors}
        name="sessionDate"
        message={translate('selectDateValidation')}
      />
    </div>
  );
};

export default SingleSessionDatePicker;
